import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import AuthService from '../../../services/auth.service';
import { logout } from '../../../actions/auth';
import { useAuth } from '../../../hooks/useAuth';
import axios from 'axios';
import config from '../../../config.json';
import {
  Box,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
} from '@mui/material';
import Logout from '@mui/icons-material/Logout';
import OrdersIcon from '@mui/icons-material/ShoppingCart'; // Appropriate icon for "Orders"
import DashboardIcon from '@mui/icons-material/Dashboard'; // Appropriate icon for "Dashboard"
import { deepPurple } from '@mui/material/colors';
const placeholder = "https://backend.gomaids.ai/public/images/users/user_placeholder.png";

const avatarColors = [
  '#1f7a8c', // Teal
  '#022b3a', // Dark Blue
  '#e1477e', // Pink
  '#f25c05', // Orange
  '#8e44ad', // Purple
  '#27ae60', // Green
  '#e74c3c', // Red
  '#3498db', // Blue
  '#f39c12', // Yellow
  '#9b59b6', // Violet
  '#1abc9c', // Turquoise
  '#34495e', // Dark Gray
  '#e67e22', // Carrot
  '#95a5a6', // Silver
  '#16a085', // Green Sea
  '#2980b9', // Belize Blue
  '#8e44ad', // Wisteria
  '#2c3e50', // Midnight Blue
  '#f1c40f', // Sun Flower
  '#d35400', // Pumpkin
];

// Function to generate consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];

  // Create a simple hash from the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get color index
  const colorIndex = Math.abs(hash) % avatarColors.length;
  return avatarColors[colorIndex];
};

export default function PopoverPopupState(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user, isLoggedIn } = useAuth();

  if (config.Debug) {
    // Debug logging
    console.log('useAuth hook result:', { user, isLoggedIn });
    console.log('localStorage user:', localStorage.getItem('user'));
  }
  // Fallback to localStorage if Redux state is empty
  const currentUser =
    user ||
    (() => {
      try {
        const localUser = JSON.parse(localStorage.getItem('user'));
        if (localUser) {
          if (config.Debug) {
            console.log('Using localStorage user as fallback:', localUser);
          }
          return localUser;
        }
      } catch (error) {
        console.error('Error parsing localStorage user:', error);
      }
      return null;
    })();

  const { image: profileImg, username, email, fullName } = currentUser || {};
  const [avatarSrc, setAvatarSrc] = React.useState(
    profileImg ? `${config.SERVER_URL}/${profileImg}` : placeholder
  );
  
  // Monitor user data changes
  useEffect(() => {
    if (config.Debug) {
      console.log('UserAccount component - user data changed:', currentUser);
      console.log('Redux auth state:', { user, isLoggedIn });
    }
  }, [currentUser, user, isLoggedIn]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleItemClick = (path) => {
    handleClose();
    history.push(path); // Navigate to Userdashboard
  };
  // Function to get initials (up to 2 characters)
  const getInitials = (name) => {
    if (!name) return 'U';

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    // Take first letter of first and last word
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };
  const displayName = fullName || username || 'User';
  const avatarColor = getAvatarColor(displayName);
  const initials = getInitials(displayName);
  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title='Account settings'>
          <IconButton
            onClick={handleClick}
            size='small'
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
          >
           
            <Avatar
              sx={{
                width: 45,
                height: 45,
                bgcolor: !profileImg ? avatarColor : "transparent",
                color: 'white',
              }}
              src={avatarSrc}
              onError={() => {
                setAvatarSrc(placeholder);  // Fallback image
              }}
            >
              {!profileImg && initials}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        sx={{
          minWidth: 250, // Increase width directly on Menu component
          '& .MuiPaper-root': {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User info header */}
        <MenuItem
          onClick={handleClose}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingY: 2,
            paddingX: 2,
          }}
        >
          <Box display='flex' alignItems='center'>
            <Avatar sx={{ bgcolor: avatarColor }}>{initials}</Avatar>
            <Box>
              <Typography variant='body1' fontWeight='bold'>
                {fullName || 'Username'}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {email || 'user@example.com'}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleItemClick('/Userdashboard')}>
          <ListItemIcon>
            <DashboardIcon fontSize='small' />
          </ListItemIcon>
          Dashboard
        </MenuItem>

        <Divider />
        <MenuItem
          onClick={() => {
            dispatch(logout());
            history.push('/login');
          }}
        >
          <ListItemIcon>
            <Logout fontSize='small' />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
